/**
 * i18n.ts — translation boundary.
 *
 * v1 ships English-only. The first argument to t() is the English source string
 * (with optional {placeholder} markers). It is returned verbatim with placeholders
 * interpolated — there is no lookup catalog in v1.
 *
 *   ✅ t('Save member')                              → 'Save member'
 *   ✅ t('Hello, {name}', { name: 'Brenda' })        → 'Hello, Brenda'
 *   ❌ t('member.save')                              → 'member.save' (no catalog yet)
 *
 * Per frontend-conventions.md §13: every user-facing string in JSX goes through t().
 *
 * When a real i18n library (i18next, react-intl) is added later, the English source
 * strings here become catalog keys (or are extracted into a catalog by tooling) —
 * either way, only this module changes; JSX call sites stay the same.
 */

type Interpolation = Record<string, string | number>;

/**
 * t('Hello, {name}', { name }) — returns the source string with {placeholder}s
 * substituted from `vars`. v1 has no catalog; the source string IS the output.
 */
export function t(source: string, vars?: Interpolation): string {
  if (!vars) return source;
  return source.replace(/\{(\w+)\}/g, (match, varName: string) => {
    const value = vars[varName];
    return value === undefined ? match : String(value);
  });
}

/**
 * The user's current locale. Reads navigator.language for v1.
 * Later sourced from user preferences.
 */
export function currentLocale(): string {
  if (typeof navigator === 'undefined') return 'en-US';
  return navigator.language || 'en-US';
}
